/**
 * Storage Service
 * Gestisce il salvataggio dei dati con supporto per sessioni temporanee o persistenti
 */

class StorageService {
  private static instance: StorageService;
  private useSessionStorage: boolean = false;

  private constructor() {
    // Controlla se l'utente ha scelto "Remember Me"
    this.checkRememberMe();
  }

  static getInstance(): StorageService {
    if (!StorageService.instance) {
      StorageService.instance = new StorageService();
    }
    return StorageService.instance;
  }

  /**
   * Controlla se usare sessionStorage o localStorage
   */
  private checkRememberMe(): void {
    // Se c'è un flag "rememberMe" in localStorage, usa localStorage
    // Altrimenti usa sessionStorage per logout automatico alla chiusura
    const rememberMe = localStorage.getItem('rememberMe') === 'true';
    this.useSessionStorage = !rememberMe;
  }

  /**
   * Setta il flag Remember Me
   */
  setRememberMe(value: boolean): void {
    if (value) {
      localStorage.setItem('rememberMe', 'true');
      // Migra i dati da sessionStorage a localStorage
      this.migrateToLocalStorage();
    } else {
      localStorage.removeItem('rememberMe');
      // Migra i dati da localStorage a sessionStorage
      this.migrateToSessionStorage();
    }
    this.useSessionStorage = !value;
  }

  /**
   * Ottiene lo storage appropriato
   */
  private getStorage(): Storage {
    return this.useSessionStorage ? sessionStorage : localStorage;
  }

  /**
   * Salva un elemento
   */
  setItem(key: string, value: string): void {
    this.getStorage().setItem(key, value);
  }

  /**
   * Recupera un elemento
   */
  getItem(key: string): string | null {
    // Prova prima nello storage corrente
    let value = this.getStorage().getItem(key);
    
    // Se non trovato e stiamo usando sessionStorage, 
    // prova in localStorage (potrebbe essere una sessione Remember Me precedente)
    if (!value && this.useSessionStorage) {
      value = localStorage.getItem(key);
      // Se trovato, migralo a sessionStorage
      if (value && key !== 'rememberMe') {
        sessionStorage.setItem(key, value);
        localStorage.removeItem(key);
      }
    }
    
    return value;
  }

  /**
   * Rimuove un elemento
   */
  removeItem(key: string): void {
    // Rimuovi da entrambi per sicurezza
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  }

  /**
   * Pulisce tutto lo storage
   */
  clear(): void {
    // Preserva il flag rememberMe
    const rememberMe = localStorage.getItem('rememberMe');
    
    localStorage.clear();
    sessionStorage.clear();
    
    // Ripristina il flag rememberMe se esisteva
    if (rememberMe) {
      localStorage.setItem('rememberMe', rememberMe);
    }
  }

  /**
   * Migra i dati da sessionStorage a localStorage
   */
  private migrateToLocalStorage(): void {
    const keys = ['token', 'refreshToken', 'codiceFiscale', 'user', 'persist:root'];
    keys.forEach(key => {
      const value = sessionStorage.getItem(key);
      if (value) {
        localStorage.setItem(key, value);
        sessionStorage.removeItem(key);
      }
    });
  }

  /**
   * Migra i dati da localStorage a sessionStorage
   */
  private migrateToSessionStorage(): void {
    const keys = ['token', 'refreshToken', 'codiceFiscale', 'user', 'persist:root'];
    keys.forEach(key => {
      const value = localStorage.getItem(key);
      if (value) {
        sessionStorage.setItem(key, value);
        localStorage.removeItem(key);
      }
    });
  }

  /**
   * Verifica se la sessione è attiva
   */
  isSessionActive(): boolean {
    const token = this.getItem('token');
    return !!token;
  }

  /**
   * Ottiene info sulla sessione corrente
   */
  getSessionInfo(): {
    isActive: boolean;
    isPersistent: boolean;
    storage: 'session' | 'local';
  } {
    return {
      isActive: this.isSessionActive(),
      isPersistent: !this.useSessionStorage,
      storage: this.useSessionStorage ? 'session' : 'local'
    };
  }
}

// Export singleton instance
const storageService = StorageService.getInstance();
export default storageService;